import { useEffect, useRef, useState } from 'react';
import LogoSpin from '../../../shared/animations/LogoSpin';
import './OAuthCallback.css';
import './PostLoginLoader.css';

const STEPS = [
  { id: 1, label: 'Verified' },
  { id: 2, label: 'Loading session' },
  { id: 3, label: 'Checking setup' },
  { id: 4, label: 'Ready' },
];

const PostLoginLoader = () => {
  const bgCanvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const [activeStep, setActiveStep] = useState(2);

  useEffect(() => {
    const canvas = bgCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, sweepAngle = 0, scanY = 0, frame = 0;

    const particles = Array.from({ length: 70 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.6 + 0.4,
      vx: (Math.random() - 0.5) * 0.22,
      vy: (Math.random() - 0.5) * 0.22,
      a: Math.random() * 0.45 + 0.08,
      pulse: Math.random() * Math.PI * 2,
    }));

    // Shooting stars
    const stars = Array.from({ length: 5 }, () => resetStar({}));
    function resetStar(s) {
      s.x = Math.random() * window.innerWidth;
      s.y = Math.random() * window.innerHeight * 0.5;
      s.len = Math.random() * 80 + 40;
      s.speed = Math.random() * 3 + 2;
      s.a = Math.random() * 0.5 + 0.3;
      s.angle = Math.PI / 4 + (Math.random() - 0.5) * 0.3;
      s.life = 0;
      s.maxLife = s.len / s.speed;
      return s;
    }

    function resize() {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    function draw() {
      frame++;
      ctx.clearRect(0, 0, W, H);

      // Grid — slightly brighter than OAuthCallback
      ctx.lineWidth = 0.5;
      const gs = 80;
      for (let x = 0; x < W; x += gs) {
        ctx.strokeStyle = 'rgba(148,4,195,0.03)';
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      for (let y = 0; y < H; y += gs) {
        ctx.strokeStyle = 'rgba(148,4,195,0.03)';
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }

      // Sweep ring — rotates around center
      const cx = W / 2, cy = H / 2;
      const sweepR = Math.min(W, H) * 0.38;
      sweepAngle = (sweepAngle + 0.008) % (Math.PI * 2);
      const grad = ctx.createConicalGradient
        ? null // not standard — use manual arc sweep below
        : null;
      // Draw a glowing arc that sweeps
      const arcLen = Math.PI * 0.55;
      for (let i = 0; i < 30; i++) {
        const t = i / 30;
        const angle = sweepAngle - arcLen * t;
        ctx.beginPath();
        ctx.arc(cx, cy, sweepR, angle, angle + 0.04);
        ctx.strokeStyle = `rgba(148,4,195,${0.18 * (1 - t)})`;
        ctx.lineWidth = 2 - t * 1.5;
        ctx.stroke();
      }
      // Bright tip of sweep
      ctx.beginPath();
      ctx.arc(cx, cy, sweepR, sweepAngle - 0.02, sweepAngle + 0.02);
      ctx.strokeStyle = 'rgba(176,79,240,0.8)';
      ctx.lineWidth = 2.5;
      ctx.shadowColor = '#9404C3';
      ctx.shadowBlur = 18;
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Second smaller ring, counter-rotating
      const sweepR2 = sweepR * 0.62;
      const sweep2 = -sweepAngle * 1.4;
      for (let i = 0; i < 20; i++) {
        const t = i / 20;
        const angle = sweep2 + arcLen * 0.6 * t;
        ctx.beginPath();
        ctx.arc(cx, cy, sweepR2, angle, angle + 0.06);
        ctx.strokeStyle = `rgba(148,4,195,${0.12 * (1 - t)})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Horizontal scanline
      scanY = (scanY + 0.6) % H;
      const scanGrad = ctx.createLinearGradient(0, scanY - 12, 0, scanY + 12);
      scanGrad.addColorStop(0, 'rgba(148,4,195,0)');
      scanGrad.addColorStop(0.5, 'rgba(148,4,195,0.06)');
      scanGrad.addColorStop(1, 'rgba(148,4,195,0)');
      ctx.fillStyle = scanGrad;
      ctx.fillRect(0, scanY - 12, W, 24);

      // Particles
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        p.pulse += 0.04;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 140) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(148,4,195,${0.09 * (1 - d / 140)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        const pulse = 0.7 + 0.3 * Math.sin(p.pulse);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * pulse, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(148,4,195,${p.a * pulse})`;
        ctx.fill();
      });

      // Shooting stars
      stars.forEach(s => {
        s.life++;
        if (s.life > s.maxLife) { resetStar(s); return; }
        const progress = s.life / s.maxLife;
        const headX = s.x + Math.cos(s.angle) * s.speed * s.life;
        const headY = s.y + Math.sin(s.angle) * s.speed * s.life;
        const tailX = headX - Math.cos(s.angle) * s.len * Math.min(progress * 2, 1);
        const tailY = headY - Math.sin(s.angle) * s.len * Math.min(progress * 2, 1);
        const fade = progress < 0.5 ? progress * 2 : (1 - progress) * 2;
        const sg = ctx.createLinearGradient(tailX, tailY, headX, headY);
        sg.addColorStop(0, 'rgba(148,4,195,0)');
        sg.addColorStop(1, `rgba(176,79,240,${s.a * fade})`);
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(headX, headY);
        ctx.strokeStyle = sg;
        ctx.lineWidth = 1.2;
        ctx.shadowColor = '#b04ff0';
        ctx.shadowBlur = 6;
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      animFrameRef.current = requestAnimationFrame(draw);
    }
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  useEffect(() => {
    const t1 = setTimeout(() => setActiveStep(3), 1200);
    const t2 = setTimeout(() => setActiveStep(4), 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="oauth-callback-overlay pll-overlay">
      <canvas ref={bgCanvasRef} className="oauth-bg-canvas" />
      <div className="oauth-glow-center pll-glow" />

      {/* Corner accents */}
      <div className="pll-corner pll-corner-tl" />
      <div className="pll-corner pll-corner-tr" />
      <div className="pll-corner pll-corner-bl" />
      <div className="pll-corner pll-corner-br" />

      <div className="oauth-callback-container pll-container">

        <div className="pll-badge">
          <span className="pll-badge-dot" />
          <span className="pll-badge-text">Tonic Suite</span>
        </div>

        <div className="pll-hex-wrap">
          <LogoSpin size={190} />
        </div>

        <div className="oauth-headline pll-headline">
          <h1>
            Setting up your <span className="oauth-accent">session</span>
            <span className="oauth-dots"><span>.</span><span>.</span><span>.</span></span>
          </h1>
          <p className="pll-sub">Hang tight — we're getting everything ready for you</p>
        </div>

        <div className="oauth-track pll-track">
          {STEPS.map((step, i) => {
            const isDone = step.id < activeStep;
            const isActive = step.id === activeStep;
            const stepClass = isDone
              ? 'oauth-track-step done'
              : isActive
              ? 'oauth-track-step active'
              : 'oauth-track-step';
            const lineClass = step.id < activeStep
              ? 'oauth-track-line lit pll-line-lit'
              : 'oauth-track-line';
            return (
              <div key={step.id} style={{ display: 'contents' }}>
                <div className={stepClass}>
                  <div className="oauth-track-dot pll-dot">
                    {isDone && <span className="pll-dot-check">✓</span>}
                  </div>
                  <div className="oauth-track-label">{step.label}</div>
                </div>
                {i < STEPS.length - 1 && <div className={lineClass} />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="oauth-footer pll-footer">
        <span className="oauth-footer-brand">TONIC</span>
        &nbsp;·&nbsp; Secured &nbsp;·&nbsp; End-to-End Encrypted
      </div>
    </div>
  );
};

export default PostLoginLoader;
